import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { StyledTabsContainer } from './Tabs.styles';

export interface TabItem {
  id: string;
  label: string;
  disabled?: boolean;
  content: ReactNode;
}

interface TabsContextType {
  activeTab: string;
  setActiveTab: (id: string) => void;
  tabs: TabItem[];
  orientation: 'horizontal' | 'vertical';
  variant: 'default' | 'underline';
}

const TabsContext = createContext<TabsContextType | undefined>(undefined);

export const useTabsContext = () => {
  return useContext(TabsContext);
};

export interface TabsProps {
  defaultTab?: string;
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
  orientation?: 'horizontal' | 'vertical';
  variant?: 'default' | 'underline';
  children: ReactNode;
  tabs: TabItem[];
  className?: string;
}

const Tabs: React.FC<TabsProps> = ({
  defaultTab,
  activeTab: controlledActiveTab,
  onTabChange,
  orientation = 'horizontal',
  variant = 'default',
  children,
  tabs,
  className,
}) => {
  const [internalActiveTab, setInternalActiveTab] = useState<string>(
    defaultTab || tabs[0]?.id || ''
  );

  const isControlled = controlledActiveTab !== undefined;
  const activeTab = isControlled ? controlledActiveTab : internalActiveTab;

  const handleTabChange = useCallback((tabId: string) => {
    if (!isControlled) {
      setInternalActiveTab(tabId);
    }
    onTabChange?.(tabId);
  }, [isControlled, onTabChange]);

  const contextValue: TabsContextType = {
    activeTab,
    setActiveTab: handleTabChange,
    tabs,
    orientation,
    variant,
  };

  return (
    <TabsContext.Provider value={contextValue}>
      <StyledTabsContainer
        className={className}
        orientation={orientation}
      >
        {children}
      </StyledTabsContainer>
    </TabsContext.Provider>
  );
};

export default Tabs;